/**
 * Request Deduplication
 * 
 * Prevents duplicate API calls by caching pending requests.
 * If an identical request is made within the deduplication window,
 * the existing promise is returned instead of making a new request.
 * 
 * Benefits:
 * - Prevents duplicate API calls during React double-render (Strict Mode)
 * - Reduces server load
 * - Improves performance and UX
 * - Automatic cleanup when requests complete
 * 
 * @module requestDedup
 */

import { REQUEST_DEDUP_CONFIG, IS_PRODUCTION } from '../constants';

/**
 * Represents a pending request with its promise and timestamp
 */
interface PendingRequest {
  promise: Promise<unknown>;
  timestamp: number;
}

/**
 * Map of request keys to pending requests
 * Key format: "METHOD:URL:BODY"
 */
const pendingRequests = new Map<string, PendingRequest>();

/**
 * Generate unique key for request
 * 
 * Combines method, URL, and body to create a unique identifier.
 * Identical requests will have identical keys.
 * 
 * @param method - HTTP method (GET, POST, etc.)
 * @param url - Full request URL
 * @param body - Request body (optional)
 * @returns Unique key for this request
 * 
 * @todo Security & Performance Improvement: Hash the request body instead of using raw string
 *   - Current implementation stores full request body in dedup key
 *   - Issues: Exposes sensitive data (PII, payment info) in memory, large memory footprint
 *   - Solution: Use FNV-1a or similar fast hash to create fixed-size fingerprint
 *   - Benefits: No sensitive data exposure, smaller keys, faster lookups
 *   - Reference: https://github.com/your-org/your-repo/issues/XXX
 * 
 * @example
 * ```typescript
 * const key1 = generateRequestKey('GET', '/api/bookings');
 * // "GET:/api/bookings:"
 * 
 * const key2 = generateRequestKey('POST', '/api/bookings', JSON.stringify({ name: 'Test' }));
 * // "POST:/api/bookings:{"name":"Test"}"
 * ```
 */
export function generateRequestKey(
  method: string,
  url: string,
  body?: BodyInit | null
): string {
  // Convert body to string if present
  const bodyStr = body ? String(body) : '';
  
  // The body goes after a newline: neither a URL nor JSON carries one, so
  // `describeRequestKey` can drop the body without parsing. A body can hold a
  // credential, so it must never reach a log line.
  return bodyStr ? `${method.toUpperCase()}:${url}\n${bodyStr}` : `${method.toUpperCase()}:${url}`;
}

/** The key without its body — what a log line may carry. */
export function describeRequestKey(key: string): string {
  const bodyAt = key.indexOf('\n');
  return bodyAt === -1 ? key : key.slice(0, bodyAt);
}

/**
 * Get or create deduplicated request
 * 
 * If an identical request is already pending (within DEDUP_WINDOW_MS),
 * returns the existing promise instead of making a new request.
 * 
 * Otherwise, executes the request function and caches its promise.
 * 
 * **Automatic Cleanup:** Promise is removed from cache after it settles.
 * 
 * @param key - Unique key for request (from generateRequestKey)
 * @param requestFn - Function that makes the actual request
 * @returns Promise with response (either cached or new)
 * 
 * @example
 * ```typescript
 * const key = generateRequestKey('GET', '/api/bookings');
 * 
 * // First call: executes requestFn
 * const result1 = await deduplicateRequest(key, () => fetch('/api/bookings'));
 * 
 * // Second call within 1s: returns cached promise
 * const result2 = await deduplicateRequest(key, () => fetch('/api/bookings'));
 * 
 * // result1 === result2 (same promise)
 * ```
 */
export function deduplicateRequest<T>(
  key: string,
  requestFn: () => Promise<T>
): Promise<T> {
  // Deduplication disabled, always execute
  if (!REQUEST_DEDUP_CONFIG.ENABLED) {
    return requestFn();
  }
  
  const now = Date.now();
  const existing = pendingRequests.get(key);
  
  // Return existing request if within dedup window
  if (existing && now - existing.timestamp < REQUEST_DEDUP_CONFIG.DEDUP_WINDOW_MS) {
    if (!IS_PRODUCTION) {
      console.log(
        `[ApiClient] ♻️ Deduplicating request (cache hit)`,
        `\nKey: ${describeRequestKey(key)}`,
        `\nAge: ${now - existing.timestamp}ms`
      );
    }
    return existing.promise as Promise<T>;
  }
  
  // Cache expired or doesn't exist, create new request
  if (!IS_PRODUCTION && existing) {
    console.log(
      `[ApiClient] 🔄 Cache expired (${now - existing.timestamp}ms > ${REQUEST_DEDUP_CONFIG.DEDUP_WINDOW_MS}ms)`,
      `\nKey: ${describeRequestKey(key)}`
    );
  }
  
  // Create new request
  const promise = requestFn();
  pendingRequests.set(key, { promise, timestamp: now });
  
  // Clean up after request completes (success or error).
  //
  // The `.catch()` sits on the END of this chain, not on `promise` itself:
  // `.finally()` derives a NEW promise that rejects whenever `promise` does, and
  // that derived one is returned to nobody. Without the catch it is an unhandled
  // rejection on every failed request — a network blip, a timeout, or the
  // PARSE_ERROR a gateway's HTML 502 produces. The caller still receives the
  // original rejection through the returned `promise`, so nothing is swallowed.
  void promise
    .finally(() => {
      const current = pendingRequests.get(key);

      // Only delete if this is still the current promise
      // (another request might have replaced it)
      if (current?.promise === promise) {
        pendingRequests.delete(key);

        if (!IS_PRODUCTION) {
          console.log(
            `[ApiClient] 🧹 Cleaned up request cache`,
            `\nKey: ${describeRequestKey(key)}`
          );
        }
      }
    })
    .catch(() => {
      /* Owned by the caller via the returned promise. */
    });
  
  return promise;
}

/**
 * Clear all pending requests
 * 
 * **Use with caution:** This will not cancel the actual HTTP requests,
 * only clears the cache. Subsequent identical requests will NOT be deduplicated.
 * 
 * **Primary use case:** Testing and cleanup
 * 
 * @example
 * ```typescript
 * // In tests:
 * afterEach(() => {
 *   clearPendingRequests();
 * });
 * ```
 */
export function clearPendingRequests(): void {
  const count = pendingRequests.size;
  pendingRequests.clear();
  
  if (!IS_PRODUCTION && count > 0) {
    console.warn(
      `[ApiClient] ⚠️ Manually cleared ${count} pending request(s) from cache`
    );
  }
}

/**
 * Get count of pending requests (for testing/debugging)
 * 
 * @returns Number of pending requests in cache
 * 
 * @example
 * ```typescript
 * console.log(`Pending requests: ${getPendingRequestCount()}`);
 * ```
 */
export function getPendingRequestCount(): number {
  return pendingRequests.size;
}

/**
 * Check if a specific request is pending
 * 
 * @param key - Request key to check
 * @returns true if request is in cache
 * 
 * @example
 * ```typescript
 * const key = generateRequestKey('GET', '/api/bookings');
 * if (isPendingRequest(key)) {
 *   console.log('Request is already pending');
 * }
 * ```
 */
export function isPendingRequest(key: string): boolean {
  return pendingRequests.has(key);
}
